import type { DeskLang, DeskStory } from "@chonburi/shared";
import { DESK_LIMITS, deskPriority } from "./mayorDesk";

/**
 * Translate a headline in the reader's own browser.
 *
 * The production API is a Cloudflare Worker, and the public translation
 * services answer a Worker's shared datacenter address with "too many
 * requests" — every request, from that address, for reasons that have
 * nothing to do with Chonburi. The same request from an ordinary network
 * address answers in a second. So the desk translates what the API could
 * not, here, in the reader's browser, and keeps the result: a published
 * headline never changes, so it is translated once per device.
 *
 * The rules the desk holds to do not change with where the translation
 * happened: a machine translation is labelled as one, and the publisher's
 * original stays beside it.
 */

const CACHE_KEY = "chonburi:desk-translations";
/** A device only ever sees this many headlines at a time. */
const CACHE_LIMIT = 400;
const TIMEOUT_MS = 9_000;
/** A successful translation is kept for a month. */
const SUCCESS_TTL_MS = 30 * 86_400_000;
/** A failure is kept for an hour, so a throttled network is not hammered. */
const FAILURE_TTL_MS = 3_600_000;
/** How many requests may be in flight at once. */
const CONCURRENCY = 3;

/** The languages the mayor reads without a translation. */
const READABLE: DeskLang[] = ["th", "en"];

/** The two renderings a headline can have: the one the mayor reads, and the English one. */
export type Rendering = "th" | "en";

interface CacheEntry {
  /** The translation, or null when the service could not produce one. */
  text: string | null;
  expiresAt: number;
}

type Cache = Record<string, CacheEntry>;

/** localStorage throws in a sandboxed frame and does not exist in a test. */
function storage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function readCache(): Cache {
  const store = storage();
  if (!store) return {};
  try {
    const raw = store.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Cache) : {};
  } catch {
    return {};
  }
}

function writeCache(cache: Cache): void {
  const store = storage();
  if (!store) return;
  try {
    const keys = Object.keys(cache);
    if (keys.length > CACHE_LIMIT) {
      // Drop the oldest half — the order is insertion order.
      for (const key of keys.slice(0, keys.length - CACHE_LIMIT / 2)) delete cache[key];
    }
    store.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // A full or blocked store is not a reason to fail the desk.
  }
}

function cacheKey(text: string, from: DeskLang, to: Rendering): string {
  return `${from}|${to}|${text}`;
}

/** The services hand back HTML entities in the translated string. */
function decodeEntities(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

/** MyMemory — open to browsers, and the one that answers an ordinary address. */
async function mymemory(text: string, from: DeskLang, to: Rendering): Promise<string | null> {
  const url =
    `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}` +
    `&langpair=${encodeURIComponent(from)}|${encodeURIComponent(to)}`;
  const res = await fetch(url, {
    headers: { accept: "application/json" },
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });
  if (!res.ok) return null;
  const body = (await res.json()) as {
    responseData?: { translatedText?: string };
    responseStatus?: number;
    quotaFinished?: boolean;
  };
  // A spent quota comes back as a 200 with an error string in the same field.
  if (body.responseStatus !== 200 || body.quotaFinished) return null;
  const translated = body.responseData?.translatedText?.trim();
  return translated && translated.length > 1 ? decodeEntities(translated) : null;
}

/** Google Translate's public endpoint — the second try. */
async function google(text: string, from: DeskLang, to: Rendering): Promise<string | null> {
  const url =
    `https://translate.googleapis.com/translate_a/single?client=gtx` +
    `&sl=${encodeURIComponent(from)}&tl=${encodeURIComponent(to)}&dt=t&q=${encodeURIComponent(text)}`;
  const res = await fetch(url, {
    headers: { accept: "application/json" },
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });
  if (!res.ok) return null;
  const body = (await res.json()) as unknown;
  if (!Array.isArray(body) || !Array.isArray(body[0])) return null;
  const translated = body[0]
    .map((chunk: unknown) =>
      Array.isArray(chunk) && typeof chunk[0] === "string" ? (chunk[0] as string) : "",
    )
    .join("")
    .trim();
  return translated.length > 1 ? decodeEntities(translated) : null;
}

/**
 * Translate through the services in turn.
 *
 * Two, because one is not enough: one runs out of quota and the other is
 * occasionally rate-limited, and the foreign coverage is the whole reason
 * the desk exists.
 */
async function translateOne(text: string, from: DeskLang, to: Rendering): Promise<string | null> {
  for (const provider of [mymemory, google]) {
    try {
      const out = await provider(text, from, to);
      if (out) return out;
    } catch {
      // Try the next one.
    }
  }
  return null;
}

/** Does this story still need a Thai headline — the one the mayor reads? */
export function needsThai(story: DeskStory): boolean {
  return !READABLE.includes(story.lang) && !story.titleTh;
}

/** Does this story still need an English rendering for the English text? */
export function needsEnglish(story: DeskStory): boolean {
  return !story.titleEn;
}

/** What the desk is missing, for one story. */
export function missingRenderings(story: DeskStory): Rendering[] {
  const out: Rendering[] = [];
  if (needsThai(story)) out.push("th");
  if (needsEnglish(story)) out.push("en");
  return out;
}

/**
 * The stories the browser should try to render, in the order it should try.
 *
 * Exactly what can reach the screen: the lead, the foreign headlines that
 * could fill the world compartment, and the headlines the mayor reads in
 * Thai but a partner abroad may need in English. Anything further down the
 * list is a headline that would not have been read this morning, and asking
 * a public translation service for it spends an allowance counted per
 * address — an allowance that is not ours to spend.
 */
export function pickTranslationCandidates(stories: DeskStory[], now: Date): DeskStory[] {
  const ranked = stories
    .map((story) => ({ story, priority: deskPriority(story, now) }))
    .sort(
      (a, b) =>
        b.priority - a.priority || b.story.publishedAt.localeCompare(a.story.publishedAt),
    );

  const picked: DeskStory[] = [];
  const seen = new Set<string>();
  const take = (story: DeskStory) => {
    if (seen.has(story.id)) return;
    if (!missingRenderings(story).length) return;
    seen.add(story.id);
    picked.push(story);
  };

  // The lead first: it is the first line of both texts.
  if (ranked[0]) take(ranked[0].story);

  // Then the foreign headlines, which are the ones the API could not render
  // and the ones the world compartment exists for.
  let world = 0;
  for (const { story } of ranked) {
    if (world >= DESK_LIMITS.world + 1) break;
    if (READABLE.includes(story.lang)) continue;
    take(story);
    world++;
  }

  // Then the headlines the mayor reads in Thai, for the English text.
  let readable = 0;
  for (const { story } of ranked) {
    if (readable >= DESK_LIMITS.lead + DESK_LIMITS.coverage) break;
    if (!READABLE.includes(story.lang)) continue;
    take(story);
    readable++;
  }

  return picked;
}

/** The result of one browser-side translation pass. */
export interface ClientTranslation {
  /** The stories that came back with a rendering. */
  stories: DeskStory[];
  /** How many headlines gained a Thai rendering, per source language. */
  byLanguage: Record<string, number>;
  /** How many renderings could not be produced. */
  failures: number;
}

/**
 * Fill in the renderings the desk is missing, in the reader's browser.
 *
 * `stories` must already be in desk order — most important first — because
 * the budget is spent on the top of the list. A desk shows a handful of
 * headlines, so a browser never translates more than it needs, and the
 * anonymous allowance the translation services count per address is left
 * for the next reader rather than spent on the twentieth headline.
 *
 * `maxJobs` counts requests, not stories: a foreign headline needs two — a
 * Thai rendering for the desk and an English one for the English text — and
 * the budget is what keeps a browser from asking a public service for more
 * than it needs.
 */
export async function translateMissing(stories: DeskStory[], maxJobs = 12): Promise<ClientTranslation> {
  const cache = readCache();
  const gained = new Map<string, Partial<Record<Rendering, string>>>();
  let failures = 0;

  const jobs: { id: string; from: DeskLang; to: Rendering; text: string }[] = [];
  for (const story of stories) {
    for (const to of missingRenderings(story)) {
      if (jobs.length >= maxJobs) break;
      jobs.push({ id: story.id, from: story.lang, to, text: story.title });
    }
    if (jobs.length >= maxJobs) break;
  }
  if (!jobs.length) return { stories: [], byLanguage: {}, failures: 0 };

  let next = 0;
  const worker = async () => {
    while (next < jobs.length) {
      const job = jobs[next++];
      const key = cacheKey(job.text, job.from, job.to);
      const hit = cache[key];
      let text: string | null;
      if (hit && hit.expiresAt > Date.now()) {
        text = hit.text;
      } else {
        text = await translateOne(job.text, job.from, job.to);
        cache[key] = {
          text,
          expiresAt: Date.now() + (text ? SUCCESS_TTL_MS : FAILURE_TTL_MS),
        };
      }
      if (!text) {
        failures++;
        continue;
      }
      const current = gained.get(job.id) ?? {};
      current[job.to] = text;
      gained.set(job.id, current);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, () => worker()),
  );

  if (jobs.length) writeCache(cache);

  const byLanguage: Record<string, number> = {};
  const out = stories.map((story) => {
    const rendered = gained.get(story.id);
    if (!rendered) return story;
    if (rendered.th) byLanguage[story.lang] = (byLanguage[story.lang] ?? 0) + 1;
    const next: DeskStory = {
      ...story,
      titleTh: rendered.th ?? story.titleTh,
      titleEn: rendered.en ?? story.titleEn,
      // The Thai rendering is now a machine translation if it was not the
      // publisher's own words.
      translation: rendered.th ? "machine" : story.translation,
      // The failure reason is stale once the browser has succeeded.
      note: rendered.th ? undefined : story.note,
    };
    return next;
  });

  return { stories: out, byLanguage, failures };
}
